/**
 * Helper functions for ADRDefinitionPage to reduce cognitive complexity
 */

import { ADRDefinition, ADRDefinitionStatus, ADRSource, ADRDefinitionUpsert } from '../../types/adr';
import { ProjectItem } from '../../types/project';

/**
 * Check if auto-generation of suggestions should occur.
 *
 * For brownfield projects the `definitionsLength` guard is relaxed: even when
 * ADR definitions already exist, suggestions should be loaded from the stored
 * conversion result so the user always sees the SAHLD-derived proposals.
 *
 * @param isLoadingDefinitions - Whether definitions are still being fetched.
 * @param suggestionsLength    - Number of suggestions already present.
 * @param isGeneratingSuggestions - Whether a generation request is in-flight.
 * @param definitionsLength    - Number of saved ADR definitions.
 * @param isBrownfield         - True when the project is a brownfield type.
 */
export const shouldAutoGenerateSuggestions = (
  isLoadingDefinitions: boolean,
  suggestionsLength: number,
  isGeneratingSuggestions: boolean,
  definitionsLength: number,
  isBrownfield = false,
): boolean => {
  if (isLoadingDefinitions || suggestionsLength > 0 || isGeneratingSuggestions) {
    return false;
  }
  // Brownfield: auto-generate regardless of existing definitions so the
  // SAHLD-sourced suggestions always appear on page load.
  if (isBrownfield) { return true; }
  // Greenfield: only auto-generate when no definitions have been saved yet.
  return definitionsLength === 0;
};

/**
 * Check if auto-save indicator should be shown
 */
export const shouldShowAutoSave = (
  hasUnsavedChanges: boolean,
  editingADR: ADRDefinition | null,
): boolean => {
  return hasUnsavedChanges && !!editingADR;
};

/**
 * Check if ADR can be saved
 */
export const canSaveADR = (
  editingADR: ADRDefinition | null,
  projectId: string | undefined,
  adrContent: string,
): boolean => {
  return !!(editingADR && projectId && adrContent);
};

/**
 * Check if ADR is currently generating
 */
export const isADRGenerating = (
  adrId: string,
  generatingADRIds: Set<string>,
): boolean => {
  return generatingADRIds.has(adrId);
};

/**
 * Check if generate all button should be disabled
 */
export const isGenerateAllDisabled = (
  isGeneratingADRs: boolean,
  generatingADRIdsSize: number,
): boolean => {
  return isGeneratingADRs || generatingADRIdsSize > 0;
};

/**
 * Check if there are defined ADRs
 */
export const hasDefinedADRs = (definitions: ADRDefinition[]): boolean => {
  return definitions.filter(d => d.status === ADRDefinitionStatus.DEFINED).length > 0;
};

/**
 * Build project context string for ADR suggestions
 */
export const buildProjectContext = (project: ProjectItem | null): string => {
  let projectContext = 'Architecture project';
  
  if (project) {
    projectContext = `${project.title}`;
    
    if (project.description?.trim()) {
      projectContext += ` - ${project.description}`;
    }
    
    if (project.project_type) {
      const projectTypeLabel = project.project_type === 'green_field' ? 'greenfield' : 'brownfield';
      projectContext += ` (${projectTypeLabel} project)`;
    }
  }
  
  return projectContext;
};

/**
 * Create definition from form data
 */
export const createDefinitionFromForm = (formData: {
  title: string;
  llmInstructions: string;
  contextDescription: string;
}): ADRDefinitionUpsert => ({
  title: formData.title,
  llmInstructions: formData.llmInstructions,
  contextDescription: formData.contextDescription,
  status: ADRDefinitionStatus.DEFINED,
  source: ADRSource.USER_CREATED,
});

/**
 * Format form data for editing
 */
export const formatFormDataForEdit = (definition: ADRDefinition) => ({
  title: definition.title,
  llmInstructions: definition.llmInstructions,
  contextDescription: definition.contextDescription ?? '',
});

/**
 * Get initial form data
 */
export const getInitialFormData = () => ({
  title: '',
  llmInstructions: '',
  contextDescription: '',
});

/**
 * The structured sections extracted from a generated ADR markdown document,
 * used to populate the My ADRs table columns.
 */
export interface ADRSections {
  context: string;
  decision: string;
  consequence: string;
}

const EMPTY_ADR_SECTIONS: ADRSections = {
  context: '',
  decision: '',
  consequence: '',
};

// Heading aliases (lower-cased) mapped to the section they populate. The ADR
// Generation Agent emits "## Context", "## Decision", "## Consequence"; a few
// legacy/plural variants are accepted for robustness.
const ADR_HEADING_ALIASES: Record<string, keyof ADRSections> = {
  context: 'context',
  decision: 'decision',
  consequence: 'consequence',
  consequences: 'consequence',
};

const ADR_HEADING_REGEX = /^#{1,6}\s+(.+?)\s*$/;

/**
 * Extract the Context, Decision and Consequence sections from ADR markdown.
 *
 * Walks the document heading-by-heading; content under a recognized heading is
 * assigned to the matching section until the next heading appears.
 *
 * @param markdown - The full generated ADR markdown (may be empty/undefined).
 * @returns The parsed sections (empty strings when a section is absent).
 */
export const parseADRSections = (
  markdown: string | undefined | null,
): ADRSections => {
  if (!markdown?.trim()) {
    return { ...EMPTY_ADR_SECTIONS };
  }

  const result: ADRSections = { ...EMPTY_ADR_SECTIONS };
  let currentKey: keyof ADRSections | null = null;
  let buffer: string[] = [];

  const flush = () => {
    if (currentKey) {
      result[currentKey] = buffer.join('\n').trim();
    }
    buffer = [];
  };

  for (const line of markdown.split('\n')) {
    const headingMatch = line.match(ADR_HEADING_REGEX);
    if (headingMatch) {
      flush();
      const headingText = headingMatch[1].trim().toLowerCase();
      currentKey = ADR_HEADING_ALIASES[headingText] ?? null;
      continue;
    }
    if (currentKey) {
      buffer.push(line);
    }
  }
  flush();

  return result;
};

/**
 * Format an ADR reference number from a 1-based index
 * (e.g. 1 -> "ADR-Archie-001"). The numeric counter is the suffix so the
 * identifier reads as a namespaced, zero-padded reference.
 * @param index - The 1-based position of the ADR in the list.
 * @returns The zero-padded ADR reference string.
 */
export const formatADRReference = (index: number): string =>
  `ADR-Archie-${String(index).padStart(3, '0')}`;

